import React, { useState, useEffect, useCallback } from 'react';
import { Button, Icon, Segment, List } from 'semantic-ui-react';
import { useWhatsappClient } from './whatsapp.client';
import { InComponentErrorRenderer } from '../../components/InComponentErrorRenderer';
const WHATSAPP_APP_ID = process.env.REACT_APP_WHATSAPP_APP_ID;
const WHATSAPP_CONFIGURATION_ID = process.env.REACT_APP_WHATSAPP_CONFIGURATION_ID;
export const WhatsappConnect = () => {
    const whatsappClient = useWhatsappClient();
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fbLoaded, setFbLoaded] = useState(false);
    const [code, setCode] = useState(null);
    const [businessIdentity, setBusinessIdentity] = useState(null);
    const loadIntegrations = async () => {
        try {
            setLoading(true);
            const data = await whatsappClient.getIntegrations();
            setIntegrations(data);
        }
        catch (err) {
            setError('Failed to load WhatsApp integration');
        }
        finally {
            setLoading(false);
        }
    };
    // Set up message event listener for WhatsApp embedded signup
    const handleMessage = useCallback((event) => {
        if (!event.origin.endsWith('facebook.com'))
            return;
        try {
            const data = JSON.parse(event.data);
            if (data.type === 'WA_EMBEDDED_SIGNUP') {
                console.log('WhatsApp signup message event: ', data);
                const eventType = data.event;
                if (eventType !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
                    if (eventType === 'CANCEL') {
                        if (data.data?.current_step) { // User abandoned at a specific step
                            // @TODO: Collect this error message for internal tracking.
                            console.error("WhatsApp signup abandoned at step:", data?.current_step);
                        }
                        else if (data.data?.error_message && data.data?.error_message) {
                            // @TODO: Collect this error message for internal tracking.
                            console.error("WhatsApp signup failed due to user reported errors:", data?.data);
                        }
                    }
                    return setError('Something went wrong. Please try again.');
                }
                const businessIdentityObj = {
                    phoneNumberId: data.data.phone_number_id,
                    whatsAppBusinessAccountId: data.data.waba_id,
                    whatsAppBusinessPortfolioId: data.data.business_id
                };
                setBusinessIdentity(businessIdentityObj);
            }
            // @TODO Handle cancelled & abandoned flows at this point, from here->  https://developers.facebook.com/docs/whatsapp/embedded-signup/implementation
        }
        catch {
            console.log('WhatsApp message event: ', event.data);
        }
    }, []);
    // Load Facebook SDK
    useEffect(() => {
        const loadFacebookSDK = () => {
            if (window.FB) {
                setFbLoaded(true);
                return;
            }
            // Create script element for Facebook SDK
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            // Set up Facebook initialization
            window.fbAsyncInit = () => {
                window.FB.init({
                    appId: WHATSAPP_APP_ID,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: 'v23.0'
                });
                setFbLoaded(true);
            };
            document.head.appendChild(script);
        };
        loadFacebookSDK();
        window.addEventListener('message', handleMessage);
        // Load existing integrations
        loadIntegrations();
        // Cleanup
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);
    useEffect(() => {
        if (!businessIdentity || !code) {
            console.error("No business identity or code found, not exchanging token", businessIdentity, code);
            return;
        }
        exchangeCodeForToken(businessIdentity, code).then(() => {
            loadIntegrations();
        });
    }, [code, businessIdentity]);
    const exchangeCodeForToken = async (businessIdentity, code) => {
        try {
            await whatsappClient.saveIntegration(businessIdentity, code);
        }
        catch (err) {
            setError('Failed to exchange token');
        }
    };
    const handleDisconnect = async (whatsAppBusinessAccountId) => {
        try {
            setLoading(true);
            await whatsappClient.disconnectIntegration(whatsAppBusinessAccountId);
            await loadIntegrations(); // Reload the list
        }
        catch (err) {
            setError('Failed to disconnect WhatsApp Business Account integration');
        }
        finally {
            setLoading(false);
        }
    };
    // WhatsApp Business API login callback
    const fbLoginCallback = (response) => {
        if (response.authResponse) {
            const code = response.authResponse.code;
            console.log('WhatsApp auth response: ', code);
            setCode(code);
        }
        else {
            console.log('WhatsApp auth response: ', response);
            setError('Failed to authenticate with WhatsApp');
        }
    };
    // Launch WhatsApp signup
    const launchWhatsAppSignup = useCallback(() => {
        if (!window.FB || !fbLoaded || !WHATSAPP_CONFIGURATION_ID) {
            console.log('Facebook SDK not loaded yet. Please try again.');
            setError('Oh Snap! We could not load this page at the moment. Please try again.');
            return;
        }
        window.FB.login(fbLoginCallback, {
            config_id: WHATSAPP_CONFIGURATION_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
                setup: {},
                featureType: 'whatsapp_business_app_onboarding', // Replace with your feature type
                sessionInfoVersion: '3',
            }
        });
    }, [fbLoaded, fbLoginCallback, WHATSAPP_CONFIGURATION_ID]);
    // const sendTestMessage = useCallback(() => {
    //   console.log('Sending test message');
    //   fetch('https://graph.facebook.com/v22.0/852388894617345/messages', {
    //     method: 'POST',
    //     headers: {
    //       'Authorization': 'Bearer EAASToLt4GJsBPjVNy6tRMZAZA2pcJFsu7aiL6KGw0fd0dDPZApqyF7smqEogr3PR0uUiqMZC2KcsMNUnt1HbL9UV0br3NZAdDZAG2nbnudFXihtx5auDFIDA0F1fKIAXQeUfBfyVnqSdm8MaXAGRugV0L7YCC0GHtpo4otgFwfxccsbVKxNAyLcCOeAjb1eGiZApwREqcpjyZCZA1oY9JMMT0SZBDle75P9d5604M2B9mu6cfD3wZDZD',
    //       'Content-Type': 'application/json'
    //     },
    //     body: JSON.stringify({
    //       'messaging_product': 'whatsapp',
    //       'to': '917832006867',
    //       'type': 'template',
    //       'template': {
    //         'name': 'hello_world',
    //         'language': {
    //           'code': 'en_US'
    //         }
    //       }
    //     })
    //   })
    //   .then(response => response.json())
    //   .then(data => console.log(data))
    //   .catch(error => console.error('Error:', error));
    // }, []);
    return (React.createElement(Segment, { style: { boxShadow: 'none', border: '1px solid #f0f0f0' } },
        error && React.createElement(InComponentErrorRenderer, { error: error }),
        integrations.length === 0 ? (React.createElement("div", null,
            React.createElement("p", { style: { color: '#666', marginBottom: '1em' } }, "Connect your Whatsapp Business account to enable form submissions through WhatsApp."),
            React.createElement(Button, { color: "blue", icon: true, labelPosition: "left", onClick: launchWhatsAppSignup, loading: loading || !fbLoaded, disabled: loading || !fbLoaded, style: { backgroundColor: '#1877f2', border: 0, borderRadius: 4, color: '#fff', cursor: 'pointer',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: "16px", fontWeight: 'bold', height: "40px", padding: "0 24px"
                } },
                React.createElement(Icon, { name: "whatsapp" }),
                "Login with Facebook"))) : (React.createElement("div", null,
            React.createElement("p", { style: { color: '#666', marginBottom: '1em' } }, "Connected WhatsApp Business Accounts:"),
            React.createElement(List, { divided: true }, integrations.map((integration) => (React.createElement(List.Item, { key: integration.whatsAppBusinessAccountId, style: { padding: '0.5em 0' } },
                React.createElement(List.Content, { floated: "right" },
                    React.createElement(Button, { size: "tiny", color: "red", icon: "trash", onClick: () => handleDisconnect(integration.whatsAppBusinessAccountId), loading: loading, disabled: loading })),
                React.createElement(List.Content, null,
                    React.createElement(List.Header, null,
                        React.createElement(Icon, { name: "whatsapp", color: "purple" }),
                        integration.whatsAppBusinessAccountId),
                    React.createElement(List.Description, { style: { fontSize: '0.85em', color: '#666' } },
                        "Connected on ",
                        new Date(integration.installedAt).toLocaleDateString(),
                        integration.isActive ? (React.createElement("span", { style: { color: '#28a745', marginLeft: '0.5em' } }, "\u2022 Active")) : (React.createElement("span", { style: { color: '#dc3545', marginLeft: '0.5em' } }, "\u2022 Inactive"))))))))))));
};
